import { ASComponentSize, ASLoaderType } from '@eleventheye/asui';
import { GameWeek } from '@shared/constants';
import { GameID, ModelVersionID, SeasonID, SeasonWeekID } from '@shared/types';
import { CategoryRankingsNavBarState } from 'app/scenes/statsmanager/components/categoryrankings/components/statsnav/CategoryRankingsNavBar';

// #region ADMIN UI TYPES *****************************************************
export const AdminActionName = {
  /** Action triggers `Cloud Function` to fetch the NFL season schedule via `RapidAPI` and update `FirebaseDB` with the data it received from `RapidAPI` */
  LoadRegularSeasonSchedule: 'Load Regular Season Schedule',

  /** Action generates new game context enrichment to be used in Predictive Analysis */
  GenerateGameContextEnrichment: 'Generate Game Context',
  /** Action generates teams `strength of schedule` to be used in Predictive Analysis */
  GenerateStrengthOfSchedule: 'Generate Strength Of Schedule',
  /** Action generates predictions for a given game, based on already existing data */
  GeneratePredictionsForGame: 'Generate Predictions For Game',

  /** Action triggers `Cloud Function` to fetch the box score for a regular season NFL game, and generates `IGameStats[]`, `IEnhancedTeamStats[]`, and `ITeamStandings[]` records in `FirebaseDB`  */
  LoadGameBoxScore: 'Load Game BoxScore',
  /** Actions triggers `Cloud Function` to calculate weekly standings `ITeamStandings[]` for a regular season NFL week, or it can generate standings only for the selected games GameID[] in a given week */
  CalculateWeeklyStandings: 'Calculate Weekly Standings',
  /** Action generates `RankingsPayload[]` a `Weekly` and `Season` rankings when games for a given regular season NFL week have been played */
  GenerateRankings: 'Generate Rankings',

  /** Action uses a new parser to generate all `67+` categories of stats per match for both team */
  GenerateNewStats: 'Generate New Stats',
  /** Action generates new weekly rankings record from new Team Stats and Cumulative category models */
  GenerateNewWeeklyRankings: 'Generate New Weekly Rankings',
  /** Action copies weekly 'new' ranks into `{YEAR}-Ranks` collection where each team contains only its own ranks. Basically this is almost the same as `{YEAR}-Teams` collection but instead of weekly values, it stores weekly ranks */
  WriteRanksByTeam: 'Write Ranks By Team',
  /** Action generates team `consistency` metrics and saves the data in `Firebase DB` */
  GenerateTeamConsistency: 'Generate Team Consistency',
  /** Action populates already `existing` prediction models with the `actual results` for a given week/game  */
  SetPredictionsWithActualScores: 'Set Predictions With Actual Scores',
  /** Action fetches all `IPredictions` with the actual score and calculates the `Accuracy Metrics` totals. */
  CalculatePredictionAccuracyMetrics: 'Calculate Prediction Accuracy Metrics',
  /** Action generates head-to-head game results `HeadToHeadGameT` for a given season based on `GamesWithScoreIDs` and writes the results in `Firebase DB` `/headtohead` collection */
  GenerateHeadToHeadGames: 'Generate Head To Head Games',
  /** Action updates schedule records in `Firebase` of type `IMatchup` with score props based on `IGameStats`. The props are, `homePts`, `awayPts`, `homeResult`, `awayResult`, and `lineScore` */
  GenerateScheduleWithScores: 'Generate Schedule With Scores',
  /** Action creates bye weeks for each season as an array of 32 teams, formatted as `YYYY-WW-<TeamID>-<TeamAbrv>` */
  CreateByeWeeks: 'Create Bye Weeks',
  /** Action backfills the `SyncManager` document for a given season with completed data */
  BackfillSyncManager: 'Backfill Sync Manager',
  /** Action generates GameWithScoreIDs for a given set of completed games and writes the data in `Firebase DB` under `stats/${yearID}` */
  GenerateGameWithScoreIDs: 'Generate Game With Score IDs',
  /** Action uses a `STAT_CATEGORIES_ABBR` list to generate all category models and save them in FB */
  GenerateCategories: 'Generate Categories',

  /** Action updates team match data to include `passing` and `rushing` yards allowed */
  GenerateUpdatedTeamMatchResults: 'Update Team MatchResults',
  /** Action parses the full regular season schedule and generates `Array<UID>` list of games for each team. Each list of scheduled games is then added to the matching `team` under the `scheduleByYear[year]` prop */
  GenerateTeamSeasonSchedule: 'Generate Team Season Schedule',
  /** Action copies `FirebaseDB` stat2024week1, stat2024week2, ... collections into `stats/2024/2024-01/*` sub collections in order to test ease of access and the ability to fetch items easier from the backend */
  CopyStatsCollectionIntoSubCollection: 'Copy Stats into Sub Collection',
  /** Action sets the list of `gameID` for a specific season, if week is set to `all` or season and a week */
  SetGameIDsBySeasonWeek: 'Set Game IDs by Season Week',
  /** Action moves the schedule data for a specific season into a sub-collection under the stats collection `stats/${year}/schedule` */
  MoveScheduleAsSubCollectionUnderStatsCollection: 'Move Schedule as a SubCol Under StatsCol',
  /** Action migrates schedule data from all teams located in `ITeamDetails.schedule` to `ITeamDetails.scheduleByYear.[year]`. This way we can support multi-year data easier */
  FixTeamsScheduleStructure: 'Fix Teams Schedule Structure',
  /** Action updates the `UIDs` of each standings records from old `UID` without the leading `YYYY` to the new `${year}-${uID}` format */
  UpdateStandingsUIDs: 'Update Standings UIDs',
  /** Action updates the `UIDs` of each teammatchresults records from old `UID` without the leading `YYYY` to the new `${year}-${uID}` format */
  UpdateTeamMatchResultsUIDs: 'Update Team Match Results UIDs',
  /** Action updates the `UIDs` of each teamdetails records from old `UID` without the leading `YYYY` to the new `${year}-${uID}` format */
  UpdateTeamDetailsUIDs: 'Update Team Details UIDs',
  /** Action writes predictions into the schedule for a given season and model version */
  WritePredictionsIntoSchedule: 'Write Predictions Into Schedule',
} as const;

export type AdminActionNameT = (typeof AdminActionName)[keyof typeof AdminActionName];

export type AdminActionState = {
  action: AdminAction;
  year: number | string;
  week: ScheduleForWeek | GameWeek;
  season: SeasonType;
  modifiedOn?: number;
  gameIDs?: GameID[];
  modelVersionID?: ModelVersionID;
};

export type AdminActionWithAuditLogState = {
  auditlog?: AdminActionState[];
  adminActionName?: AdminActionNameT;
} & AdminActionState;

export enum AdminAction {
  Copied = 'copied',
  Copy = 'copy',
  Error = 'error',
  Idle = 'idle',
  Load = 'load',
  Loaded = 'loaded',
  LoadedPartial = 'loaded-partial',
  Remove = 'remove',
  Update = 'update',
  Updated = 'updated',
  UpdatedPartial = 'updated-partial',
}

export enum SeasonType {
  Regular = 'reg',
  Post = 'post',
  Pre = 'pre',
  All = 'all',
}

// #endregion *****************************************************************

// #region APP UP PROPS + DEPS ************************************************
export type LoaderDetails = {
  visible: boolean;
  size?: ASComponentSize;
  type?: ASLoaderType;
  isModal?: boolean;
};

export type WebWorkerType = {
  status: string;
  isEnabled: boolean;
};

export type SeasonWeeks =
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14'
  | '15'
  | '16'
  | '17'
  | '18';

export type ScheduleForWeek = 'all' | SeasonWeeks;

export type SeasonalGameIDMap = Record<SeasonWeekID, GameID[]>;

export type AppUIProps = {
  isIndexDBInitialized: boolean;
  routeToLoadAfterAuth: string;
  applicationInitialized: boolean;
  showAuthentication: boolean;
  showUILibrary: boolean;
  showStarterScreen: boolean;
  showBackButton: boolean;
  modalVisible: boolean;
  displayCode: boolean;
  categoryRankingsNavBarState: CategoryRankingsNavBarState;
  showTimeNavigator: boolean;
  loader: LoaderDetails;
  admin: {
    adminAction: AdminActionWithAuditLogState;
    gameIDsMap: Record<SeasonID, SeasonalGameIDMap>;
  };
  showAdminActions: boolean;
  webworker: WebWorkerType;
};

export type ActionSelector = {
  setValue: (value: string) => void;
  value: string;
  id: string;
  options: string[];
};

// #endregion
